<template>
  <div class="layout-container">
    <Header />
    <main class="layout-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="$route.fullPath" />
        </transition>
      </router-view>
    </main>
    <Footer ref="footerRef" />

    <!-- 返回顶部气泡球 -->
    <div
      v-if="showBackToTop"
      class="back-to-top"
      @click="scrollToTop"
      :class="{ 'back-to-top-visible': showBackToTop }"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m18 15-6-6-6 6"/>
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import Header from '@/components/layout/Header.vue';
import Footer from '@/components/layout/Footer.vue';

const route = useRoute();
const footerRef = ref<InstanceType<typeof Footer> | null>(null);
const showBackToTop = ref(false); // 控制返回顶部按钮显示/隐藏

// 监听滚动事件
const handleScroll = () => {
  // 滚动超过300px时显示返回顶部按钮
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  showBackToTop.value = scrollTop > 300;
};

// 监听路由变化，滚动到顶部
watch(
  () => route.path,
  () => {
    // 等待下一个tick确保DOM已更新
    nextTick(() => {
      scrollToTop();
    });
  }
);

// 滚动到顶部的函数
const scrollToTop = () => {
  // 平滑滚动到顶部
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });

  // 如果有滚动容器，也滚动到顶部
  const mainElement = document.querySelector('.layout-content');
  if (mainElement) {
    mainElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  // 隐藏返回顶部按钮
  showBackToTop.value = false;
};

// 暴露触发底部动画的方法
const triggerFooterAnimation = () => {
  footerRef.value?.triggerFooterAnimation();
};

// 挂载时添加滚动监听
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  // 初始检查一次滚动位置
  handleScroll();
});

// 卸载时移除滚动监听
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

defineExpose({
  triggerFooterAnimation,
  scrollToTop
});
</script>

<style scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.layout-content {
  flex: 1;
  overflow-y: auto;
}

/* 可选的过渡效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 返回顶部按钮样式 */
.back-to-top {
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #FFFFFF 0%, rgb(99.6, 64.8, 43.2) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  transform: translateY(20px);
}

.back-to-top-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.back-to-top:hover {
  background-color: #66b1ff;
  transform: translateY(-2px);
}

/* 适配移动端 */
@media (max-width: 768px) {
  .back-to-top {
    width: 40px;
    height: 40px;
    bottom: 20px;
    right: 20px;
  }
}
</style>